import React, { useState } from 'react';
import { ContentRow } from '../../../types/homepage';
import CardGrid, { CardStyle, GridLayout, CardItem } from './CardGrid';

export interface BlockTheme {
    primaryColor?: string;
    primaryText?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    surfaceColor?: string;
    textColor?: string;
    mutedColor?: string;
    borderColor?: string;
    fontFamily?: string;
    cardRadius?: string;
}

const DEFAULT_THEME: Required<BlockTheme> = {
    primaryColor: '#00897b',
    primaryText: '#ffffff',
    secondaryColor: '#f0fdf9',
    backgroundColor: '#f5f5f5',
    surfaceColor: '#ffffff',
    textColor: '#111827',
    mutedColor: '#6b7280',
    borderColor: '#e5e7eb',
    fontFamily: "'DM Sans', sans-serif",
    cardRadius: '10px',
};

function useThemeCss(theme: BlockTheme | undefined, prefix: string): string {
    const t = { ...DEFAULT_THEME, ...theme };
    return `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Syne:wght@600;700;800&display=swap');
    .${prefix} {
      --primary:    ${t.primaryColor};
      --primary-t:  ${t.primaryText};
      --secondary:  ${t.secondaryColor};
      --bg:         ${t.backgroundColor};
      --surface:    ${t.surfaceColor};
      --text:       ${t.textColor};
      --muted:      ${t.mutedColor};
      --border:     ${t.borderColor};
      --font:       ${t.fontFamily};
      --radius:     ${t.cardRadius};
      font-family:  ${t.fontFamily};
    }
  `;
}

function SectionHeader({
    title, subtitle, ctaLabel, ctaUrl, onShowAll, rowId,
}: {
    title?: string | null;
    subtitle?: string | null;
    ctaLabel?: string;
    ctaUrl?: string;
    onShowAll?: (id: string) => void;
    rowId?: string;
}) {
    if (!title && !subtitle) return null;
    return (
        <div className="blk-header">
            <div>
                {title && <h2 className="blk-title">{title}</h2>}
                {subtitle && <p className="blk-subtitle">{subtitle}</p>}
            </div>
            <div className="blk-header-right">
                {ctaLabel && (
                    <a href={ctaUrl || '#'} className="blk-cta-pill">{ctaLabel}</a>
                )}
                {onShowAll && rowId && (
                    <button className="blk-show-all" onClick={() => onShowAll(rowId)}>
                        Show All
                    </button>
                )}
            </div>
        </div>
    );
}

const SHARED_CSS = `
  .blk-header {
    display: flex; align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 16px; gap: 12px;
  }
  .blk-title {
    font-family: var(--font);
    font-size: 22px; font-weight: 700;
    color: var(--text); margin: 0 0 2px;
    letter-spacing: -0.01em;
  }
  .blk-subtitle {
    font-size: 13.5px; color: var(--muted); margin: 0;
    font-family: var(--font);
  }
  .blk-header-right {
    display: flex; align-items: center; gap: 12px; flex-shrink: 0;
  }
  .blk-cta-pill {
    display: inline-flex; align-items: center;
    padding: 7px 18px;
    background: var(--primary); color: var(--primary-t);
    font-family: var(--font); font-size: 13px; font-weight: 700;
    border-radius: 100px; border: none; cursor: pointer;
    text-decoration: none; white-space: nowrap;
    transition: filter .18s, transform .18s;
  }
  .blk-cta-pill:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .blk-show-all {
    font-size: 14px; font-weight: 700; color: var(--text);
    text-decoration: none; cursor: pointer;
    background: none; border: none; font-family: var(--font);
    white-space: nowrap; padding: 0;
    transition: color .15s;
  }
  .blk-show-all:hover { color: var(--primary); }
  .blk-section {
    padding: 28px 0;
    background: var(--bg);
  }
  .blk-inner {
    max-width: 1440px; margin: 0 auto; padding: 0 24px;
  }
  @media (max-width: 640px) { .blk-inner { padding: 0 16px; } }
`;

// SEARCH & FILTER BLOCK

interface SearchFilterProps {
    row: ContentRow;
    theme?: BlockTheme;
    onSearch?: (query: string) => void;
    onFilterChange?: (filterKey: string, value: string) => void;
}

export const SearchFilterBlock: React.FC<SearchFilterProps> = ({
    row,
    theme,
    onSearch,
    onFilterChange,
}) => {
    const { title, settings } = row;
    const css = useThemeCss(theme, 'sf-root');

    const [searchQuery, setSearchQuery] = useState('');
    const [dropdownValues, setDropdownValues] = useState<Record<string, string>>({});

    const dropdowns: Array<{ key: string; label: string; options: string[] }> =
        (settings?.dropdowns as any[]) ?? [];

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch?.(searchQuery);
    };

    const handleDropdown = (key: string, value: string) => {
        setDropdownValues((prev) => ({ ...prev, [key]: value }));
        onFilterChange?.(key, value);
    };

    return (
        <>
            <style>{`
                ${css}
                .sf-root.sf-section {
                    background: var(--surface);
                    border-bottom: 1px solid var(--border);
                    padding: 14px 0 12px;
                }
                .sf-inner { max-width: 1440px; margin: 0 auto; padding: 0 24px; }
                .sf-title-row {
                    display: flex; align-items: center; justify-content: space-between;
                    gap: 16px; margin-bottom: 10px;
                }
                .sf-title {
                    font-family: var(--font); font-size: 11px; font-weight: 700;
                    letter-spacing: 0.08em; text-transform: uppercase;
                    color: var(--muted); margin: 0; flex-shrink: 0;
                }
                .sf-search-wrap { position: relative; flex: 0 0 260px; }
                .sf-search-input {
                    width: 100%; padding: 7px 36px 7px 12px;
                    border: 1.5px solid var(--border); border-radius: 6px;
                    font-family: var(--font); font-size: 13px; color: var(--text);
                    background: var(--surface); outline: none;
                    transition: border-color .18s, box-shadow .18s; box-sizing: border-box;
                }
                .sf-search-input::placeholder { color: var(--muted); }
                .sf-search-input:focus { border-color: var(--primary); }
                .sf-search-btn {
                    position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
                    background: none; border: none; padding: 0;
                    cursor: pointer; color: var(--muted);
                    display: flex; align-items: center; transition: color .15s;
                }
                .sf-search-btn:hover { color: var(--primary); }
                .sf-search-btn svg { width: 15px; height: 15px; }
                .sf-filters-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
                .sf-select-wrap { position: relative; flex: 1; min-width: 120px; max-width: 200px; }
                .sf-select {
                    width: 100%; appearance: none; -webkit-appearance: none;
                    padding: 7px 28px 7px 11px; border: 1.5px solid var(--border);
                    border-radius: 6px; font-family: var(--font); font-size: 12.5px;
                    color: var(--text); background: var(--surface); cursor: pointer; outline: none;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .sf-select-chevron {
                    position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
                    pointer-events: none; color: var(--muted); display: flex; align-items: center;
                }
                .sf-select-chevron svg { width: 12px; height: 12px; }
                .sf-divider { width: 1px; height: 20px; background: var(--border); flex-shrink: 0; }
                @media (max-width: 900px) {
                    .sf-title-row { flex-direction: column; align-items: flex-start; }
                    .sf-search-wrap { flex: 1 1 100%; }
                    .sf-select-wrap { flex: 1 1 140px; }
                }
                @media (max-width: 600px) {
                    .sf-inner { padding: 0 16px; }
                    .sf-filters-row { gap: 6px; }
                    .sf-divider { display: none; }
                }
            `}</style>

            <section className="sf-root sf-section">
                <div className="sf-inner">
                    <div className="sf-title-row">
                        {title && <p className="sf-title">{title}</p>}
                        <form className="sf-search-wrap" onSubmit={handleSearch}>
                            <input
                                className="sf-search-input"
                                type="text"
                                placeholder={(settings?.searchPlaceholder as string) || 'Search...'}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search"
                            />
                            <button type="submit" className="sf-search-btn" aria-label="Submit search">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                                </svg>
                            </button>
                        </form>
                    </div>

                    {dropdowns.length > 0 && (
                        <div className="sf-filters-row">
                            {dropdowns.map((dd, i) => (
                                <React.Fragment key={dd.key}>
                                    {i > 0 && <div className="sf-divider" aria-hidden="true" />}
                                    <div className="sf-select-wrap">
                                        <select
                                            className="sf-select"
                                            value={dropdownValues[dd.key] || ''}
                                            onChange={(e) => handleDropdown(dd.key, e.target.value)}
                                            aria-label={dd.label}
                                        >
                                            <option value="">{dd.label}</option>
                                            {dd.options.map((opt) => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                        <span className="sf-select-chevron" aria-hidden="true">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};


// QUICK LINKS

interface QuickLinksProps {
    row: ContentRow;
    theme?: BlockTheme;
    onChannelClick?: (id: string) => void;
    onShowAll?: (rowId: string) => void;
}

export const QuickLinksChannels: React.FC<QuickLinksProps> = ({
    row, theme, onChannelClick, onShowAll,
}) => {
    const { content, title } = row;
    const css = useThemeCss(theme, 'ql-root');

    if (!content?.length) return null;

    return (
        <>
            <style>{`
                ${css}
                .ql-root.ql-section { padding: 20px 0 24px; background: var(--surface); border-bottom: 1px solid var(--border); }
                .ql-inner { max-width: 1440px; margin: 0 auto; padding: 0 24px; }
                .ql-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; gap: 12px; }
                .ql-title { font-family: var(--font); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); margin: 0; }
                .ql-show-all { font-size: 13px; font-weight: 600; color: var(--primary); background: none; border: none; cursor: pointer; font-family: var(--font); padding: 0; text-decoration: underline; text-underline-offset: 3px; }
                .ql-grid { display: flex; gap: 12px; overflow-x: auto; scrollbar-width: none; padding-bottom: 2px; }
                .ql-grid::-webkit-scrollbar { display: none; }
                .ql-card {
                    flex: none; width: 200px; border-radius: var(--radius); overflow: hidden;
                    cursor: pointer; position: relative; aspect-ratio: 16 / 10;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.12); transition: transform .22s, box-shadow .22s;
                    background: #1a3a5c;
                }
                .ql-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
                @media (min-width: 768px) {
                    .ql-grid.ql-few-cards { overflow-x: visible; }
                    .ql-grid.ql-few-cards .ql-card { flex: 1; width: auto; min-width: 150px; }
                }
                .ql-card-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
                .ql-card:hover .ql-card-img { transform: scale(1.06); }
                .ql-card-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.52) 100%); }
                .ql-badge {
                    position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%);
                    padding: 2px 10px; background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.4);
                    border-radius: 100px; font-family: var(--font); font-size: 9px; font-weight: 700;
                    letter-spacing: 0.07em; text-transform: uppercase; color: rgba(255,255,255,0.9);
                    white-space: nowrap; backdrop-filter: blur(4px);
                }
                .ql-card-title {
                    position: absolute; bottom: 10px; left: 0; right: 0; text-align: center; padding: 0 10px;
                    font-family: var(--font); font-size: 13px; font-weight: 700; color: #ffffff;
                    line-height: 1.25; text-shadow: 0 1px 6px rgba(0,0,0,0.5); margin: 0;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
                }
                .ql-card-no-img { position: absolute; inset: 0; background: var(--primary); display: flex; align-items: center; justify-content: center; }
                .ql-card-no-img-icon { font-size: 32px; opacity: 0.4; }
                @media (max-width: 640px) { .ql-inner { padding: 0 16px; } .ql-card { width: 160px; } }
            `}</style>

            <section className="ql-root ql-section">
                <div className="ql-inner">
                    {(title || onShowAll) && (
                        <div className="ql-header">
                            {title && <p className="ql-title">{title}</p>}
                            {onShowAll && (
                                <button className="ql-show-all" onClick={() => onShowAll(row.id)}>Show All</button>
                            )}
                        </div>
                    )}
                    <div className={`ql-grid${content.length <= 5 ? ' ql-few-cards' : ''}`}>
                        {content.map((item: any) => {
                            const thumb = item.backgroundImage || item.thumbnail;
                            const name = item.name || item.title;
                            return (
                                <div
                                    key={item.id}
                                    className="ql-card"
                                    onClick={() => onChannelClick?.(item.slug || item.id)}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={name}
                                    onKeyDown={(e) => e.key === 'Enter' && onChannelClick?.(item.slug || item.id)}
                                >
                                    {thumb ? (
                                        <img src={thumb} alt={name} className="ql-card-img" loading="lazy" />
                                    ) : (
                                        <div className="ql-card-no-img"><span className="ql-card-no-img-icon">📁</span></div>
                                    )}
                                    <div className="ql-card-overlay" aria-hidden="true" />
                                    {item.badge && <span className="ql-badge">{item.badge}</span>}
                                    <h3 className="ql-card-title">{name}</h3>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
};

// HIGHLIGHTED CATEGORIES

interface HighlightedCategoriesBlockProps {
    row: ContentRow;
    theme?: BlockTheme;
    onCategoryClick?: (id: string) => void;
    onShowAll?: (rowId: string) => void;
}

export const HighlightedCategoriesBlock: React.FC<HighlightedCategoriesBlockProps> = ({
    row, theme, onCategoryClick, onShowAll,
}) => {
    const { content, title, settings } = row;
    const css = useThemeCss(theme, 'hcat-root');
    const layout = row.layoutStyle || 'dark_grid';

    if (!content?.length) return null;

    const isDark = layout === 'dark_grid';
    const isPill = layout === 'pill_list';

    return (
        <>
            <style>{`
                ${css}
                .hcat-root.hcat-section { padding: 20px 0 24px; background: ${isDark ? '#f0f4f8' : 'var(--bg)'}; border-bottom: 1px solid var(--border); }
                .hcat-inner { max-width: 1440px; margin: 0 auto; padding: 0 24px; }
                .hcat-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; gap: 12px; }
                .hcat-title { font-family: var(--font); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); margin: 0; }
                .hcat-show-all { font-size: 13px; font-weight: 600; color: var(--primary); background: none; border: none; cursor: pointer; font-family: var(--font); padding: 0; text-decoration: underline; text-underline-offset: 3px; }
                .hcat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; }
                .hcat-tile { position: relative; border-radius: var(--radius); overflow: hidden; cursor: pointer; aspect-ratio: 16 / 9; min-height: 80px; transition: transform .2s, box-shadow .2s; background: var(--primary); }
                .hcat-tile:hover { transform: translateY(-2px) scale(1.01); box-shadow: 0 8px 22px rgba(0,0,0,0.2); }
                .hcat-tile-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.3; transition: opacity .3s; }
                .hcat-tile:hover .hcat-tile-bg { opacity: 0.45; }
                .hcat-tile-icon-bg { position: absolute; bottom: -6px; right: 4px; font-size: 40px; opacity: 0.18; line-height: 1; user-select: none; pointer-events: none; transform: rotate(-10deg); }
                .hcat-tile-label { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 10px; text-align: center; }
                .hcat-tile-label span { font-family: var(--font); font-size: clamp(11px, 1.4vw, 14px); font-weight: 700; color: #ffffff; line-height: 1.3; text-shadow: 0 1px 4px rgba(0,0,0,0.3); }
                .hcat-tile.hcat-light { background: var(--surface); border: 1.5px solid var(--border); box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
                .hcat-tile.hcat-light .hcat-tile-label span { color: var(--text); text-shadow: none; }
                .hcat-tile.hcat-light .hcat-tile-icon-bg { opacity: 0.12; }
                .hcat-tile-accent { position: absolute; bottom: 0; left: 0; right: 0; height: 3px; border-radius: 0 0 var(--radius) var(--radius); }
                .hcat-pills { display: flex; flex-wrap: wrap; gap: 8px; }
                .hcat-pill { display: inline-flex; align-items: center; gap: 7px; padding: 8px 18px; border-radius: 100px; background: var(--primary); color: #fff; font-family: var(--font); font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: filter .18s, transform .18s; }
                .hcat-pill:hover { filter: brightness(1.12); transform: translateY(-1px); }
                .hcat-pill-icon { font-size: 15px; line-height: 1; }
                @media (max-width: 768px) { .hcat-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px; } }
                @media (max-width: 480px) { .hcat-inner { padding: 0 16px; } .hcat-grid { grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 7px; } .hcat-tile { min-height: 64px; } }
            `}</style>

            <section className="hcat-root hcat-section">
                <div className="hcat-inner">
                    {(title || onShowAll) && (
                        <div className="hcat-header">
                            {title && <p className="hcat-title">{title}</p>}
                            {onShowAll && (
                                <button className="hcat-show-all" onClick={() => onShowAll(row.id)}>Show All</button>
                            )}
                        </div>
                    )}

                    {isPill && (
                        <div className="hcat-pills">
                            {content.map((item: any) => (
                                <button
                                    key={item.id}
                                    className="hcat-pill"
                                    style={{ background: item.color || 'var(--primary)' }}
                                    onClick={() => onCategoryClick?.(item.id)}
                                >
                                    {item.icon && <span className="hcat-pill-icon">{item.icon}</span>}
                                    {item.title}
                                </button>
                            ))}
                        </div>
                    )}

                    {!isPill && (
                        <div className="hcat-grid">
                            {content.map((item: any) => (
                                <div
                                    key={item.id}
                                    className={`hcat-tile${isDark ? '' : ' hcat-light'}`}
                                    style={{ background: isDark ? (settings?.tileColor as string || 'var(--primary)') : 'var(--surface)' }}
                                    onClick={() => onCategoryClick?.(item.id)}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={item.title}
                                    onKeyDown={(e) => e.key === 'Enter' && onCategoryClick?.(item.id)}
                                >
                                    {item.backgroundImage && (
                                        <img src={item.backgroundImage} alt="" className="hcat-tile-bg" aria-hidden="true" loading="lazy" />
                                    )}
                                    {item.icon && <span className="hcat-tile-icon-bg" aria-hidden="true">{item.icon}</span>}
                                    {!isDark && (
                                        <div className="hcat-tile-accent" style={{ background: item.color || 'var(--primary)' }} />
                                    )}
                                    <div className="hcat-tile-label"><span>{item.title}</span></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};


// WELCOME BANNER

interface WelcomeBannerProps {
    row: any;
    theme?: BlockTheme;
}

export const WelcomeBanner: React.FC<WelcomeBannerProps> = ({ row, theme }) => {
    const { title, subtitle, settings } = row;
    const css = useThemeCss(theme, 'wb-root');
    const layout = row.layoutStyle || 'centered';

    const bgImage = settings?.backgroundImage as string | undefined;
    const overlayOpacity = (settings?.overlayOpacity as number) ?? 0.45;
    const bgColor = (settings?.backgroundColor as string) || 'var(--primary)';
    const titleColor = (settings?.titleColor as string) || (settings?.textColor as string) || '#000000';
    const subtitleColor = (settings?.subtitleColor as string) || (settings?.textColor as string) || '#000000';
    const ctaBgColor = (settings?.ctaBgColor as string) || '#ffffff';
    const ctaTextColor = (settings?.ctaTextColor as string) || (theme?.primaryColor || '#00897b');
    const ctaSecondaryBgColor = (settings?.ctaSecondaryBgColor as string) || 'rgba(255,255,255,0.15)';
    const ctaSecondaryTextColor = (settings?.ctaSecondaryTextColor as string) || '#ffffff';

    const ctaRow = (
        <div className="wb-cta-row">
            {settings?.ctaLabel ? (
                <a
                    href={(settings.ctaUrl as string) || '#'}
                    className="wb-cta-primary"
                    style={{ background: ctaBgColor, color: ctaTextColor }}
                >
                    {settings.ctaLabel as string}
                </a>
            ) : null}
            {settings?.ctaSecondaryLabel ? (
                <a
                    href={(settings.ctaSecondaryUrl as string) || '#'}
                    className="wb-cta-secondary"
                    style={{ background: ctaSecondaryBgColor, color: ctaSecondaryTextColor }}
                >
                    {settings.ctaSecondaryLabel as string}
                </a>
            ) : null}
        </div>
    );

    return (
        <>
            <style>{`
                ${css}
                .wb-root.wb-section {
                    position: relative; padding: 0;
                    min-height: ${layout === 'split' ? '320px' : '280px'};
                    display: flex; align-items: stretch; overflow: hidden;
                    background: ${bgImage ? 'transparent' : bgColor};
                }
                .wb-bg { position: absolute; inset: 0; z-index: 0; }
                .wb-bg img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
                .wb-bg-overlay { position: absolute; inset: 0; background: rgba(0,0,0,${overlayOpacity}); }
                .wb-inner-centered {
                    position: relative; z-index: 1; width: 100%; max-width: 860px; margin: 0 auto;
                    padding: 56px 24px; text-align: center;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                }
                .wb-inner-left {
                    position: relative; z-index: 1; width: 100%; max-width: 1440px; margin: 0 auto;
                    padding: 56px 48px; display: flex; flex-direction: column; align-items: flex-start; justify-content: center;
                }
                .wb-split { display: flex; width: 100%; min-height: 320px; position: relative; z-index: 1; }
                .wb-split-text { flex: 1; padding: 56px 48px; display: flex; flex-direction: column; justify-content: center; background: ${bgColor}; max-width: 55%; }
                .wb-split-image { flex: 1; position: relative; min-height: 100%; }
                .wb-split-image img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
                .wb-eyebrow { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; background: rgba(255,255,255,0.18); border-radius: 100px; padding: 4px 14px; margin-bottom: 14px; color: ${titleColor}; }
                .wb-title { font-family: 'Syne', sans-serif; font-size: clamp(26px, 4.5vw, 48px); font-weight: 800; line-height: 1.1; margin: 0 0 14px; letter-spacing: -0.02em; }
                .wb-subtitle { font-size: clamp(14px, 1.8vw, 17px); line-height: 1.7; opacity: 0.9; margin: 0 0 28px; max-width: 580px; }
                .wb-cta-row { display: flex; gap: 12px; flex-wrap: wrap; ${layout === 'centered' ? 'justify-content: center;' : ''} }
                .wb-cta-primary { display: inline-flex; align-items: center; gap: 8px; padding: 13px 30px; font-family: var(--font); font-size: 14px; font-weight: 700; border-radius: 8px; border: none; cursor: pointer; text-decoration: none; box-shadow: 0 4px 18px rgba(0,0,0,0.2); transition: transform .18s, box-shadow .18s, filter .18s; }
                .wb-cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.28); filter: brightness(1.05); }
                .wb-cta-secondary { display: inline-flex; align-items: center; gap: 8px; padding: 13px 26px; font-family: var(--font); font-size: 14px; font-weight: 600; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.35); text-decoration: none; cursor: pointer; backdrop-filter: blur(8px); transition: filter .18s; }
                .wb-cta-secondary:hover { filter: brightness(1.15); }
                @media (max-width: 768px) {
                    .wb-split { flex-direction: column; }
                    .wb-split-text { max-width: 100%; padding: 40px 24px; }
                    .wb-split-image { min-height: 220px; }
                    .wb-inner-left { padding: 40px 20px; }
                }
            `}</style>

            <section className="wb-root wb-section">
                {bgImage && layout !== 'split' && (
                    <div className="wb-bg">
                        <img src={bgImage} alt="" aria-hidden="true" />
                        <div className="wb-bg-overlay" />
                    </div>
                )}

                {layout !== 'split' && layout !== 'left_aligned' && (
                    <div className="wb-inner-centered">
                        {settings?.eyebrow ? <span className="wb-eyebrow">{settings.eyebrow as string}</span> : null}
                        {title && <h1 className="wb-title" style={{ color: titleColor }}>{title}</h1>}
                        {subtitle && <p className="wb-subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
                        {Boolean(settings?.ctaLabel || settings?.ctaSecondaryLabel) && ctaRow}
                    </div>
                )}

                {layout === 'left_aligned' && (
                    <div className="wb-inner-left">
                        {settings?.eyebrow ? <span className="wb-eyebrow">{settings.eyebrow as string}</span> : null}
                        {title && <h1 className="wb-title" style={{ color: titleColor }}>{title}</h1>}
                        {subtitle && <p className="wb-subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
                        {Boolean(settings?.ctaLabel || settings?.ctaSecondaryLabel) && ctaRow}
                    </div>
                )}

                {layout === 'split' && (
                    <div className="wb-split">
                        <div className="wb-split-text">
                            {settings?.eyebrow ? <span className="wb-eyebrow">{settings.eyebrow as string}</span> : null}
                            {title && <h1 className="wb-title" style={{ color: titleColor }}>{title}</h1>}
                            {subtitle && <p className="wb-subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
                            {Boolean(settings?.ctaLabel || settings?.ctaSecondaryLabel) && ctaRow}
                        </div>
                        {bgImage && (
                            <div className="wb-split-image">
                                <img src={bgImage} alt={title || 'Banner'} />
                            </div>
                        )}
                    </div>
                )}
            </section>
        </>
    );
};



// CATEGORIES LIST

interface CategoriesListProps {
    row: ContentRow;
    theme?: BlockTheme;
    onCategoryClick?: (id: string) => void;
}

export const CategoriesList: React.FC<CategoriesListProps> = ({ row, theme, onCategoryClick }) => {
    const { content, title, subtitle } = row;
    const css = useThemeCss(theme, 'cl-root');
    if (!content?.length) return null;

    const cardStyleMap: Record<string, CardStyle> = {
        grid_layout: 'icon_color',
        background_cards: 'background_image',
        horizontal_scroll: 'icon_color',
        pill_list: 'icon_color',
        minimal_list: 'minimal',
    };
    const gridLayoutMap: Record<string, GridLayout> = {
        grid_layout: 'grid',
        background_cards: 'grid',
        horizontal_scroll: 'grid',
        pill_list: 'pill_list',
        minimal_list: 'grid',
    };

    const layoutStyle = row.layoutStyle || 'grid_layout';
    const t = { ...DEFAULT_THEME, ...theme };

    return (
        <>
            <style>{`${css}${SHARED_CSS}.cl-root.cl-section{padding:28px 0;background:var(--bg);}`}</style>
            <section className="cl-root cl-section">
                <div className="blk-inner">
                    <SectionHeader title={title} subtitle={subtitle} />
                    <CardGrid
                        items={content as CardItem[]}
                        cardStyle={cardStyleMap[layoutStyle] ?? 'icon_color'}
                        gridLayout={gridLayoutMap[layoutStyle] ?? 'grid'}
                        onItemClick={onCategoryClick}
                        showCount={true}
                        primaryColor={t.primaryColor}
                        cardRadius={t.cardRadius}
                        fontFamily={t.fontFamily}
                    />
                </div>
            </section>
        </>
    );
};


// HIGHLIGHTED CHANNELS

interface HighlightedChannelsProps {
    row: ContentRow;
    theme?: BlockTheme;
    onChannelClick?: (id: string) => void;
    onShowAll?: (rowId: string) => void;
}

export const HighlightedChannels: React.FC<HighlightedChannelsProps> = ({
    row, theme, onChannelClick, onShowAll,
}) => {
    const { content, title, subtitle, settings } = row;
    const css = useThemeCss(theme, 'hc-root');
    if (!content?.length) return null;

    const cardStyleMap: Record<string, CardStyle> = {
        channel_background_cards: 'background_image',
        standard_cards: 'standard',
        minimal_list: 'minimal',
        icon_cards: 'icon_color',
        large_feature_cards: 'large_feature',
    };
    const layoutStyle = row.layoutStyle || 'channel_background_cards';
    const t = { ...DEFAULT_THEME, ...theme };

    return (
        <>
            <style>{`${css}${SHARED_CSS}.hc-root.hc-section{padding:28px 0;background:var(--bg);}`}</style>
            <section className="hc-root hc-section">
                <div className="blk-inner">
                    <SectionHeader
                        title={title} subtitle={subtitle}
                        ctaLabel={settings?.ctaLabel as string}
                        ctaUrl={settings?.ctaUrl as string}
                        onShowAll={onShowAll} rowId={row.id}
                    />
                    <CardGrid
                        items={content as CardItem[]}
                        cardStyle={cardStyleMap[layoutStyle] ?? 'background_image'}
                        gridLayout="grid"
                        onItemClick={onChannelClick}
                        showCount={true}
                        showBrowseButton={layoutStyle === 'channel_background_cards'}
                        showDescription={true}
                        primaryColor={t.primaryColor}
                        cardRadius={t.cardRadius}
                        fontFamily={t.fontFamily}
                    />
                </div>
            </section>
        </>
    );
};


// TEXT OVER VIDEO (Featured Collections)

interface TextOverVideoProps {
    row: ContentRow;
    theme?: BlockTheme;
    onVideoClick?: (id: string) => void;
    onShowAll?: (rowId: string) => void;
}

export const TextOverVideo: React.FC<TextOverVideoProps> = ({ row, theme, onVideoClick, onShowAll }) => {
    const { content, title, subtitle, settings } = row;
    const layout = row.layoutStyle || 'default';
    const css = useThemeCss(theme, 'tv-root');
    if (!content?.length) return null;

    const cols = layout === 'three_col' ? 3 : layout === 'single_hero' ? 1 : 2;

    return (
        <>
            <style>{`
        ${css}
        ${SHARED_CSS}
        .tv-root.tv-section { padding: 28px 0; background: var(--bg); }
        .tv-grid { display: grid; grid-template-columns: repeat(${cols}, 1fr); gap: 16px; }
        @media (max-width: 768px) { .tv-grid { grid-template-columns: 1fr; } }
        .tv-card { position: relative; height: ${cols === 1 ? '380px' : '260px'}; border-radius: var(--radius); overflow: hidden; cursor: pointer; box-shadow: 0 2px 10px rgba(0,0,0,0.1); transition: transform .22s, box-shadow .22s; }
        .tv-card:hover { transform: translateY(-4px); box-shadow: 0 10px 30px rgba(0,0,0,0.16); }
        .tv-img { width: 100%; height: 100%; object-fit: cover; transition: transform .35s; }
        .tv-card:hover .tv-img { transform: scale(1.06); }
        .tv-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.2) 55%, transparent 100%); }
        .tv-body { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px; }
        .tv-title { font-family: 'Syne', sans-serif; font-size: clamp(16px, 2.2vw, 22px); font-weight: 700; color: #fff; margin: 0 0 6px; line-height: 1.2; }
        .tv-desc { font-size: 13px; color: rgba(255,255,255,0.78); margin: 0 0 12px; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-family: var(--font); }
        .tv-watch-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 18px; background: var(--primary); color: var(--primary-t); font-family: var(--font); font-size: 12.5px; font-weight: 700; border-radius: 100px; border: none; cursor: pointer; transition: filter .15s; }
        .tv-watch-btn:hover { filter: brightness(1.1); }
        .tv-watch-btn svg { width: 13px; height: 13px; }
      `}</style>

            <section className="tv-root tv-section">
                <div className="blk-inner">
                    <SectionHeader title={title} subtitle={subtitle} onShowAll={onShowAll} rowId={row.id} />
                    <div className="tv-grid">
                        {content.map((item: any) => (
                            <div key={item.id} className="tv-card" onClick={() => onVideoClick?.(item.id)}>
                                <img src={item.thumbnail} alt={item.title} className="tv-img" />
                                <div className="tv-overlay" />
                                <div className="tv-body">
                                    <h3 className="tv-title">{item.title}</h3>
                                    {Boolean(item.description && settings?.showDescription) && (
                                        <p className="tv-desc">{item.description}</p>
                                    )}
                                    <button className="tv-watch-btn">
                                        <svg viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                        </svg>
                                        Watch
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};


// FEATURED CHANNEL

interface FeaturedChannelProps {
    row: ContentRow;
    theme?: BlockTheme;
    onVideoClick?: (id: string) => void;
    onChannelClick?: (id: string) => void;
}

export const FeaturedChannel: React.FC<FeaturedChannelProps> = ({
    row, theme, onVideoClick, onChannelClick,
}) => {
    const { content, title, subtitle, settings } = row;
    const css = useThemeCss(theme, 'fc-root');
    if (!content?.length) return null;

    const channel = content[0] as any;
    const videos = content.slice(1) as any[];

    return (
        <>
            <style>{`
        ${css}
        ${SHARED_CSS}
        .fc-root.fc-section { padding: 28px 0; background: var(--bg); }
        .fc-hero { position: relative; border-radius: 14px; overflow: hidden; height: 160px; margin-bottom: 20px; cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
        .fc-hero-bg { position: absolute; inset: 0; background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%); }
        .fc-hero-bg img { width: 100%; height: 100%; object-fit: cover; opacity: 0.55; }
        .fc-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 70%); }
        .fc-hero-body { position: relative; height: 100%; display: flex; align-items: center; gap: 18px; padding: 0 28px; }
        .fc-hero-logo { width: 64px; height: 64px; border-radius: 12px; border: 2.5px solid rgba(255,255,255,0.3); overflow: hidden; flex-shrink: 0; background: #e5e7eb; }
        .fc-hero-logo img { width: 100%; height: 100%; object-fit: cover; }
        .fc-hero-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--primary); font-family: var(--font); margin: 0 0 4px; }
        .fc-hero-name { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: #fff; margin: 0 0 4px; }
        .fc-hero-desc { font-size: 13px; color: rgba(255,255,255,0.75); font-family: var(--font); margin: 0; }
        .fc-hero-btn { margin-left: auto; flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px; padding: 9px 20px; background: var(--primary); color: var(--primary-t); font-family: var(--font); font-size: 13px; font-weight: 700; border-radius: 8px; border: none; cursor: pointer; transition: filter .15s; }
        .fc-hero-btn:hover { filter: brightness(1.1); }
        .fc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
        .fc-video-card { cursor: pointer; border-radius: var(--radius); overflow: hidden; background: var(--surface); border: 1px solid var(--border); box-shadow: 0 1px 4px rgba(0,0,0,0.06); transition: transform .2s, box-shadow .2s; }
        .fc-video-card:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
        .fc-thumb { position: relative; aspect-ratio: 16/9; background: #e5e7eb; overflow: hidden; }
        .fc-thumb img { width: 100%; height: 100%; object-fit: cover; transition: transform .3s; }
        .fc-video-card:hover .fc-thumb img { transform: scale(1.05); }
        .fc-dur { position: absolute; bottom: 5px; right: 6px; padding: 2px 7px; background: rgba(0,0,0,0.75); color: #fff; font-size: 11px; font-weight: 700; border-radius: 4px; font-family: var(--font); }
        .fc-video-body { padding: 10px 12px 12px; }
        .fc-video-title { font-size: 13.5px; font-weight: 700; color: var(--text); margin: 0 0 4px; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-family: var(--font); }
        .fc-video-meta { font-size: 12px; color: var(--muted); font-family: var(--font); }
      `}</style>

            <section className="fc-root fc-section">
                <div className="blk-inner">
                    <div className="fc-hero" onClick={() => onChannelClick?.(channel.id)}>
                        <div className="fc-hero-bg">
                            {(channel.backgroundImage || channel.thumbnail) && (
                                <img src={channel.backgroundImage || channel.thumbnail} alt="" />
                            )}
                        </div>
                        <div className="fc-hero-overlay" />
                        <div className="fc-hero-body">
                            {channel.logo && (
                                <div className="fc-hero-logo"><img src={channel.logo} alt={channel.name} /></div>
                            )}
                            <div>
                                <p className="fc-hero-eyebrow">Featured Channel</p>
                                <h2 className="fc-hero-name">{channel.name || title || channel.title}</h2>
                                {(subtitle || channel.description) && (
                                    <p className="fc-hero-desc">{subtitle || channel.description}</p>
                                )}
                            </div>
                            <button className="fc-hero-btn" onClick={(e) => { e.stopPropagation(); onChannelClick?.(channel.id); }}>
                                View Channel
                            </button>
                        </div>
                    </div>

                    {videos.length > 0 && (
                        <div className="fc-grid">
                            {videos.map((v) => (
                                <div key={v.id} className="fc-video-card" onClick={() => onVideoClick?.(v.id)}>
                                    <div className="fc-thumb">
                                        <img src={v.thumbnail} alt={v.title} loading="lazy" />
                                        {Boolean(settings?.showDuration && v.duration) && <span className="fc-dur">{v.duration}</span>}
                                    </div>
                                    <div className="fc-video-body">
                                        <h3 className="fc-video-title">{v.title}</h3>
                                        <p className="fc-video-meta">
                                            {settings?.showViewCount && v.views ? `${(v.views as number).toLocaleString()} views` : v.channelName}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};


// CUSTOM PROMO / HTML BLOCK

interface CustomPromoProps {
    row: ContentRow;
    theme?: BlockTheme;
}

export const CustomPromo: React.FC<CustomPromoProps> = ({ row, theme }) => {
    const { title, settings } = row;
    const css = useThemeCss(theme, 'cp-root');
    if (!settings?.htmlContent && !title) return null;

    return (
        <>
            <style>{`
        ${css}
        .cp-root.cp-section { padding: 24px; background: ${(settings?.backgroundColor as string) || 'var(--bg)'}; }
        .cp-inner { max-width: 1440px; margin: 0 auto; }
        .cp-title { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 700; color: ${(settings?.textColor as string) || 'var(--text)'}; margin: 0 0 16px; }
        .cp-html-wrap { font-family: var(--font); }
      `}</style>

            <section className="cp-root cp-section">
                <div className="cp-inner">
                    {title && <h2 className="cp-title">{title}</h2>}
                    {Boolean(settings?.htmlContent) && (
                        <div className="cp-html-wrap" dangerouslySetInnerHTML={{ __html: settings?.htmlContent as string }} />
                    )}
                </div>
            </section>
        </>
    );
};